export type DuplicateRowDisposition = 'primary' | 'rename' | 'skip';

export type DuplicateConflictLike = {
  productRows: number[];
};

export type DuplicateResolutionLike = {
  action?: 'skip' | 'replace' | 'keep_row' | 'keep_all';
  keepRow?: number;
  rowDisposition?: Record<number, DuplicateRowDisposition> | Record<string, DuplicateRowDisposition>;
};

function dispositionForRow(
  rowDisposition: DuplicateResolutionLike['rowDisposition'],
  row: number,
): DuplicateRowDisposition | undefined {
  if (!rowDisposition) return undefined;
  const map = rowDisposition as Record<string, DuplicateRowDisposition>;
  return map[row] ?? map[String(row)];
}

/** Resolve per-row disposition (legacy action/keepRow when rowDisposition omitted). */
export function resolveDuplicateRowDispositions(
  conflict: DuplicateConflictLike,
  resolution?: DuplicateResolutionLike,
): Map<number, DuplicateRowDisposition> {
  const rows = [...conflict.productRows].sort((a, b) => a - b);
  const map = new Map<number, DuplicateRowDisposition>();

  if (resolution?.rowDisposition) {
    for (const row of rows) {
      const d = dispositionForRow(resolution.rowDisposition, row);
      map.set(row, d === 'primary' || d === 'rename' || d === 'skip' ? d : 'skip');
    }
    const primaries = rows.filter((r) => map.get(r) === 'primary');
    if (primaries.length > 1) {
      const keep = primaries[0]!;
      for (const row of rows) {
        if (map.get(row) === 'primary' && row !== keep) map.set(row, 'rename');
      }
    }
    return map;
  }

  if (resolution?.action === 'keep_all') {
    for (const row of rows) {
      map.set(row, row === rows[0] ? 'primary' : 'rename');
    }
    return map;
  }

  const keepRow = resolution?.keepRow ?? rows[0]!;
  for (const row of rows) {
    map.set(row, row === keepRow ? 'primary' : 'skip');
  }
  return map;
}

/** Rows to import, in slug/name disambiguation order (primary first, then rename by line). */
export function orderedDuplicateImportRows(
  conflict: DuplicateConflictLike,
  resolution?: DuplicateResolutionLike,
): number[] {
  const dispositions = resolveDuplicateRowDispositions(conflict, resolution);
  const primary = [...dispositions.entries()].find(([, d]) => d === 'primary')?.[0];
  const renamed = [...dispositions.entries()]
    .filter(([, d]) => d === 'rename')
    .map(([row]) => row)
    .sort((a, b) => a - b);
  const importing = [...dispositions.entries()].filter(([, d]) => d !== 'skip').map(([row]) => row);
  if (importing.length === 0) return [];
  if (primary != null) return [primary, ...renamed];
  return renamed.length > 0 ? renamed : importing.sort((a, b) => a - b);
}

/** `null` = skip row; `0` = keep slug/name; `>0` = disambiguation index. */
export function duplicateRowImportIndex(
  row: number,
  conflict: DuplicateConflictLike,
  resolution?: DuplicateResolutionLike,
): number | null {
  const ordered = orderedDuplicateImportRows(conflict, resolution);
  const idx = ordered.indexOf(row);
  return idx >= 0 ? idx : null;
}

export function defaultDuplicateRowDisposition(
  conflict: DuplicateConflictLike,
): Record<number, DuplicateRowDisposition> {
  const rows = [...conflict.productRows].sort((a, b) => a - b);
  const out: Record<number, DuplicateRowDisposition> = {};
  for (const row of rows) {
    out[row] = row === rows[0] ? 'primary' : 'skip';
  }
  return out;
}

export function rowDispositionToResolution(
  slug: string,
  conflict: DuplicateConflictLike,
  rowDisposition: Record<number, DuplicateRowDisposition>,
): DuplicateResolutionLike & { slug: string } {
  const ordered = orderedDuplicateImportRows(conflict, { rowDisposition });
  if (ordered.length <= 1) {
    return {
      slug,
      action: 'keep_row',
      keepRow: ordered[0] ?? conflict.productRows[0],
      rowDisposition,
    };
  }
  return { slug, action: 'keep_all', rowDisposition };
}
